import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StaffLocation } from '@/shared/types/api';

interface LocationState {
  accessibleLocations: StaffLocation[];
  selectedLocationId: string | null; // 'all' for all locations, or specific UUID
  hasViewAllPermission: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: LocationState = {
  accessibleLocations: [],
  selectedLocationId: null, // null means use all accessible locations
  hasViewAllPermission: false,
  loading: false,
  error: null,
};

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setAccessibleLocations: (
      state,
      action: PayloadAction<{
        locations: StaffLocation[];
        hasViewAll: boolean;
      }>
    ) => {
      state.accessibleLocations = action.payload.locations;
      state.hasViewAllPermission = action.payload.hasViewAll;
      state.loading = false;
      state.error = null;

      // Auto-select primary location if available and no selection made
      if (!state.selectedLocationId && action.payload.locations.length > 0) {
        const primaryLocation = action.payload.locations.find((loc) => loc.is_primary);
        state.selectedLocationId = primaryLocation
          ? primaryLocation.location_id
          : action.payload.locations[0].location_id;
      }
    },
    setSelectedLocation: (state, action: PayloadAction<string | null>) => {
      state.selectedLocationId = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearLocationState: (state) => {
      state.accessibleLocations = [];
      state.selectedLocationId = null;
      state.hasViewAllPermission = false;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setAccessibleLocations,
  setSelectedLocation,
  setLoading,
  setError,
  clearLocationState,
} = locationSlice.actions;

export default locationSlice.reducer;
